import { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Shield,
  X,
  Send,
  HeartHandshake,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Community from "../components/Community";
import BookingDialog from "../components/BookingDialog";
import { Toaster } from "../components/ui/sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useScrollReveal } from "../lib/useScrollReveal";
import { createEnquiry } from "../lib/api";

/* ===================== Topic Selector data ===================== */
const CATEGORIES = [
  {
    key: "student",
    label: "Student",
    emoji: "🧘",
    accent: "#2a9d8f",
    bg: "#e0f5f3",
    options: [
      "Join a yoga class",
      "Class schedule enquiry",
      "Free trial request",
      "Membership & pricing",
      "Rescheduling of class",
    ],
  },
  {
    key: "instructor",
    label: "Instructor",
    emoji: "🌿",
    accent: "#7c5cbf",
    bg: "#efe9f8",
    options: [
      "Apply as a yoga instructor",
      "Apply as a volunteer or intern",
      "Guest instructor collab on social media",
    ],
  },
  {
    key: "general",
    label: "General",
    emoji: "💬",
    accent: "#e07b2a",
    bg: "#fdf0e4",
    options: ["Sponsorship", "Technical support", "Other"],
  },
];

/* ===================== Contact info cards ===================== */
const INFO_CARDS = [
  {
    Icon: Phone,
    iconBg: "#e8f0e9",
    iconColor: "#2a4a30",
    label: "Call / WhatsApp",
    value: "+91 98674 13477",
    sub: "Mon – Sun · 8 AM to 8 PM",
    href: "https://wa.me/919867413477",
  },
  {
    Icon: Mail,
    iconBg: "#fdf0e4",
    iconColor: "#c4714a",
    label: "Email Us",
    value: "hello@yogiinit.com",
    sub: "We reply within 24 hours",
    href: "mailto:hello@yogiinit.com",
  },
  {
    Icon: MapPin,
    iconBg: "#efe9f8",
    iconColor: "#7c5cbf",
    label: "Location",
    value: "Malad West, Mumbai, Maharashtra",
    sub: "Online sessions available worldwide",
    href: "#",
  },
  {
    Icon: Clock,
    iconBg: "#e8f5f3",
    iconColor: "#2a9d8f",
    label: "Session Timings",
    value: "Flexible Scheduling",
    sub: "Morning · Evening · Weekend slots",
    href: "#",
  },
];

/* ===================== 1. Page Hero ===================== */
const PageHero = () => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="contact-hero"
      className="pt-36 pb-16 md:pt-44 md:pb-20 bg-[#F7F4EF]"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
        <p className="reveal text-[11px] tracking-[0.32em] uppercase text-[#D4A24C] font-semibold mb-5">
          Get In Touch
        </p>
        <h1 className="reveal font-serif-display text-[clamp(2.4rem,5vw,4rem)] leading-[1.05] text-[#2e2318] font-light">
          We'd love to hear from{" "}
          <em className="italic font-normal text-[#1F4A38]">you</em>
        </h1>
        <p className="reveal mt-6 max-w-[460px] mx-auto text-[#5a4a3a] text-base leading-relaxed font-light">
          Whether you're ready to start your journey or just have a question —
          we're here and we'd love to connect.
        </p>
      </div>
    </section>
  );
};

/* ===================== 2. Contact Layout (Info + Form) ===================== */
const ContactLayout = () => {
  const ref = useScrollReveal();
  const [category, setCategory] = useState(null); // 'student' | 'instructor' | 'general'
  const [option, setOption] = useState(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const activeCat = CATEGORIES.find((c) => c.key === category);

  const onCategoryClick = (key) => {
    if (category === key) {
      setCategory(null);
      setOption(null);
    } else {
      setCategory(key);
      setOption(null);
    }
  };

  const resetTopic = () => {
    setCategory(null);
    setOption(null);
  };

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.firstName.trim() ||
      !form.email.trim() ||
      !form.message.trim()
    ) {
      alert("Please fill in your first name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await createEnquiry({
        type: "contact",
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        message: form.message,
        category: category || undefined,
        option: option || undefined,
        source: "contact-page",
      });
      setSent(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not send your message. Please try again.";
      alert(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      data-testid="contact-layout"
      className="bg-[#F7F4EF] pt-4 pb-24 md:pb-32"
    >
      <div className="max-w-[1080px] mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-[1fr_1.45fr] gap-10 md:gap-12">
        {/* LEFT — Info cards */}
        <div className="reveal flex flex-col gap-4">
          {INFO_CARDS.map(({ Icon, iconBg, iconColor, label, value, sub, href }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={`info-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}
              className="group bg-white rounded-2xl border border-[#2e2318]/8 px-6 py-5 flex items-start gap-4 hover:-translate-y-[3px] hover:shadow-[0_12px_30px_rgba(46,35,24,0.06)] transition-all duration-400"
            >
              <span
                className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: iconBg, color: iconColor }}
              >
                <Icon size={19} strokeWidth={1.7} />
              </span>
              <div className="min-w-0">
                <p className="text-[10.5px] tracking-[0.25em] uppercase text-[#8a7a6a] font-medium mb-1">
                  {label}
                </p>
                <p className="text-[15px] font-medium text-[#2e2318] leading-snug">
                  {value}
                </p>
                <p className="text-[12.5px] text-[#8a7a6a] mt-0.5">{sub}</p>
              </div>
            </a>
          ))}

          <blockquote className="bg-[#e8f0e9] border-l-[3px] border-[#2a4a30] rounded-xl px-5 py-4">
            <p className="font-serif-display italic text-[16.5px] text-[#1F4A38] leading-relaxed">
              "Yoga is not about touching your toes. It is about what you learn
              on the way down."
            </p>
            <footer className="mt-2 text-[10.5px] tracking-[0.22em] uppercase text-[#5a4a3a] font-medium">
              — Nitu Yadav, Founder, Yogiinit
            </footer>
          </blockquote>
        </div>

        {/* RIGHT — Form */}
        <div
          data-testid="contact-form-panel"
          className="reveal bg-white rounded-[24px] border border-[#2e2318]/8 shadow-[0_2px_32px_rgba(46,35,24,0.05)] p-7 md:p-11"
        >
          {sent ? (
            <SuccessState onReset={() => {
              setSent(false);
              setForm({ firstName: "", lastName: "", email: "", message: "" });
              resetTopic();
            }} />
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="First Name"
                  testid="first-name"
                  placeholder="Priya"
                  value={form.firstName}
                  onChange={update("firstName")}
                  required
                />
                <Field
                  label="Last Name"
                  testid="last-name"
                  placeholder="Sharma"
                  value={form.lastName}
                  onChange={update("lastName")}
                />
              </div>
              <Field
                label="Email Address"
                testid="email"
                type="email"
                placeholder="priya@email.com"
                value={form.email}
                onChange={update("email")}
                required
              />

              {/* Two-step topic selector */}
              <div>
                <Label className="text-[11px] tracking-[0.25em] uppercase text-[#5a4a3a] font-semibold">
                  I am a…
                </Label>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {CATEGORIES.map((c) => {
                    const isActive = category === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        data-testid={`cat-${c.key}`}
                        onClick={() => onCategoryClick(c.key)}
                        className="rounded-full px-5 py-2.5 text-[13.5px] font-medium border-[1.5px] transition-all duration-300 flex items-center gap-2"
                        style={
                          isActive
                            ? {
                                backgroundColor: c.bg,
                                borderColor: c.accent,
                                color: c.accent,
                              }
                            : {
                                backgroundColor: "#F7F4EF",
                                borderColor: "rgba(46,35,24,0.15)",
                                color: "#5a4a3a",
                              }
                        }
                      >
                        <span aria-hidden>{c.emoji}</span>
                        {c.label}
                      </button>
                    );
                  })}
                </div>

                {/* Step 2 — animated sub-options */}
                <div
                  className="overflow-hidden transition-[max-height,opacity] duration-[380ms] ease-out"
                  style={{
                    maxHeight: activeCat ? "320px" : "0",
                    opacity: activeCat ? 1 : 0,
                  }}
                >
                  {activeCat && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {activeCat.options.map((opt) => {
                        const selected = option === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            data-testid={`sub-${opt
                              .toLowerCase()
                              .replace(/[\s/&]+/g, "-")}`}
                            onClick={() =>
                              setOption(selected ? null : opt)
                            }
                            className="rounded-full px-4 py-2 text-[12.5px] font-medium border-[1.5px] transition-all duration-300"
                            style={
                              selected
                                ? {
                                    backgroundColor: activeCat.accent,
                                    borderColor: activeCat.accent,
                                    color: "#fff",
                                  }
                                : {
                                    backgroundColor: "transparent",
                                    borderColor: activeCat.accent,
                                    color: activeCat.accent,
                                  }
                            }
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Confirmation pill */}
                {option && activeCat && (
                  <div
                    className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-semibold"
                    style={{
                      backgroundColor: activeCat.bg,
                      color: activeCat.accent,
                    }}
                  >
                    <span>{activeCat.emoji}</span>
                    {option}
                    <button
                      type="button"
                      data-testid="clear-topic"
                      onClick={resetTopic}
                      className="ml-1 h-5 w-5 rounded-full bg-white/60 hover:bg-white flex items-center justify-center transition"
                      aria-label="Clear topic"
                    >
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <Label
                  htmlFor="contact-message"
                  className="text-[11px] tracking-[0.25em] uppercase text-[#5a4a3a] font-semibold"
                >
                  Message
                </Label>
                <Textarea
                  id="contact-message"
                  data-testid="message"
                  rows={4}
                  required
                  placeholder="Tell us how we can help you…"
                  value={form.message}
                  onChange={update("message")}
                  className="mt-2 bg-[#F7F4EF] border-[1.5px] border-[#2e2318]/12 rounded-[10px] focus-visible:border-[#2a4a30] focus-visible:ring-[3px] focus-visible:ring-[#2a4a30]/8 text-[#2e2318] placeholder:text-[#8a7a6a] min-h-[110px]"
                />
              </div>

              {/* Footer row */}
              <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-[12.5px] text-[#8a7a6a]">
                  <Shield size={14} strokeWidth={1.8} />
                  We never share your data
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="send-message-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-[#2a4a30] hover:bg-[#1e3a26] disabled:opacity-60 text-[#F7F4EF] rounded-full px-8 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-lg w-full sm:w-auto"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <Send
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, type = "text", required, ...rest }) => (
  <div>
    <Label
      htmlFor={`field-${testid}`}
      className="text-[11px] tracking-[0.25em] uppercase text-[#5a4a3a] font-semibold"
    >
      {label} {required && <span className="text-[#D4A24C]">*</span>}
    </Label>
    <Input
      id={`field-${testid}`}
      data-testid={testid}
      type={type}
      required={required}
      {...rest}
      className="mt-2 bg-[#F7F4EF] border-[1.5px] border-[#2e2318]/12 rounded-[10px] h-11 focus-visible:border-[#2a4a30] focus-visible:ring-[3px] focus-visible:ring-[#2a4a30]/8 text-[#2e2318] placeholder:text-[#8a7a6a]"
    />
  </div>
);

const SuccessState = ({ onReset }) => (
  <div
    data-testid="success-state"
    className="py-10 flex flex-col items-center text-center"
  >
    <div className="h-20 w-20 rounded-full bg-[#e8f0e9] text-[#2a4a30] flex items-center justify-center mb-6">
      <HeartHandshake size={34} strokeWidth={1.5} />
    </div>
    <h3 className="font-serif-display text-3xl text-[#2e2318]">
      Message <em className="italic text-[#1F4A38]">Sent!</em>
    </h3>
    <p className="mt-4 max-w-sm text-[#5a4a3a] leading-relaxed">
      🙏 Namaste. We've received your note and will respond personally within 24
      hours. Until then, take a deep breath.
    </p>
    <button
      type="button"
      onClick={onReset}
      data-testid="send-another"
      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#1F4A38] hover:text-[#D4A24C] transition"
    >
      Send another message →
    </button>
  </div>
);

/* ===================== 4. CTA Section ===================== */
const CTASection = ({ onBook }) => {
  const ref = useScrollReveal();
  return (
    <section
      ref={ref}
      data-testid="contact-cta"
      className="relative overflow-hidden text-[#F9F6F0] bg-[#1e3a26]"
    >
      <div className="grain absolute inset-0" />
      <div className="relative max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <p className="reveal text-[11px] tracking-[0.32em] uppercase text-[#D4A24C]/85 font-semibold mb-6">
          Begin Today
        </p>
        <h2 className="reveal font-serif-display text-[clamp(2.2rem,5vw,3.8rem)] leading-[1.05] font-light">
          Ready to Begin <em className="italic text-[#D4A24C]">Your</em> Journey?
        </h2>
        <p className="reveal mt-7 max-w-[500px] mx-auto text-[#F9F6F0]/70 font-light text-base leading-relaxed">
          Take the first step towards a healthier, more balanced you. A single
          breath changes everything.
        </p>
        <button
          type="button"
          onClick={onBook}
          data-testid="contact-cta-btn"
          className="reveal mt-10 inline-flex items-center gap-2 bg-[#D4A24C] hover:bg-[#C29344] text-[#2e2318] rounded-full px-9 py-3.5 text-sm font-semibold tracking-wide shadow-[0_18px_50px_rgba(212,162,76,0.3)] hover:shadow-[0_24px_60px_rgba(212,162,76,0.5)] transition-all duration-400 hover:-translate-y-0.5"
        >
          Book a Free Trial Class
          <span className="transition-transform group-hover:translate-x-1">→</span>
        </button>
      </div>
    </section>
  );
};

/* ===================== Page composition ===================== */
const ContactPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="App">
      <Header onBook={openBooking} />
      <main>
        <PageHero />
        <ContactLayout />
        <Community />
        <CTASection onBook={openBooking} />
      </main>
      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};

export default ContactPage;
